import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getDb } from '@data/db/database';
import type { Document, Payment, PaymentAllocation, Party } from '@data/db/types';
import { resolveActiveBusiness } from '@data/services/businessSession';
import { recordPayment } from '@data/services/paymentService';
import type { AllocationInput } from '@data/services/paymentService';
import { currentDeviceId } from '@data/sync/deviceIdentity';
import { DOCUMENTS_QUERY_ROOT } from '@features/documents/useDocuments';
import { PARTIES_QUERY_ROOT } from '@features/parties/useParties';

export const PAYMENTS_QUERY_ROOT = ['payments'] as const;
const USER = { id: 'local', name: 'You' };

export interface ReceiptRow {
  readonly payment: Payment;
  readonly allocations: readonly PaymentAllocation[];
}

export interface ReceiptData {
  readonly businessId: string;
  readonly customers: readonly Party[];
  readonly invoices: readonly Document[];
  readonly receipts: readonly ReceiptRow[];
}

export function useReceiptData() {
  const businessQuery = useQuery({
    queryKey: [...PAYMENTS_QUERY_ROOT, 'business'],
    queryFn: async () => resolveActiveBusiness(getDb() as never),
  });
  const businessId = businessQuery.data?.id ?? null;

  const data = useQuery({
    queryKey: [...PAYMENTS_QUERY_ROOT, 'receipt-data', businessId],
    enabled: businessId !== null,
    queryFn: async (): Promise<ReceiptData> => {
      const db = getDb();
      const [customers, documents, payments, allocations] = await Promise.all([
        db.parties.where('businessId').equals(businessId as string).toArray(),
        db.documents.where('businessId').equals(businessId as string).toArray(),
        db.payments.where('businessId').equals(businessId as string).toArray(),
        db.paymentAllocations.toArray(),
      ]);
      const byPayment = new Map<string, PaymentAllocation[]>();
      for (const allocation of allocations) {
        const list = byPayment.get(allocation.paymentId) ?? [];
        if (!allocation.voidedAt) list.push(allocation);
        byPayment.set(allocation.paymentId, list);
      }
      const customersOnly = customers.filter((party) => party.kind === 'CUSTOMER');
      const invoices = documents
        .filter((doc) => doc.docType === 'SALES_INVOICE')
        .map((doc) => {
          if (doc.customerId) return doc;
          const customer = customersOnly.find((party) => party.code === doc.partySnapshot.id.split(':').pop());
          return customer ? { ...doc, customerId: customer.id } : doc;
        })
        .sort((a, b) => b.docDate.localeCompare(a.docDate) || b.docNumber.localeCompare(a.docNumber));

      return {
        businessId: businessId as string,
        customers: customers.filter((party) => party.kind === 'CUSTOMER').sort((a, b) => a.name.localeCompare(b.name)),
        invoices,
        receipts: payments
          .filter((payment) => payment.direction === 'RECEIPT')
          .sort((a, b) => b.paymentDate.localeCompare(a.paymentDate) || b.paymentNumber.localeCompare(a.paymentNumber))
          .map((payment) => ({ payment, allocations: byPayment.get(payment.id) ?? [] })),
      };
    },
  });

  return { businessQuery, data };
}

export interface SaveReceiptInput {
  readonly customerId: string;
  readonly documentId: string;
  readonly paymentDate: string;
  readonly amountPaise: number;
  readonly method: Payment['method'];
  readonly reference?: string;
}

export function useSaveReceipt() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (input: SaveReceiptInput) => {
      const db = getDb();
      const business = await resolveActiveBusiness(db as never);
      if (!Number.isInteger(input.amountPaise) || input.amountPaise <= 0) {
        throw new Error('Enter an amount greater than zero, with up to two decimal places.');
      }
      if (!/^\d{4}-\d{2}-\d{2}$/.test(input.paymentDate) || Number.isNaN(Date.parse(`${input.paymentDate}T00:00:00`))) {
        throw new Error('Enter a valid receipt date.');
      }
      const customer = await db.parties.get(input.customerId);
      if (!customer || customer.businessId !== business.id || customer.kind !== 'CUSTOMER') {
        throw new Error('Choose a customer from the active business.');
      }
      const document = await db.documents.get(input.documentId);
      const legacyCustomerCode = document?.partySnapshot.id.split(':').pop();
      const belongsToCustomer = document?.customerId
        ? document.customerId === customer.id
        : legacyCustomerCode === customer.code;
      if (
        !document || document.businessId !== business.id || document.docType !== 'SALES_INVOICE' || !belongsToCustomer
      ) {
        throw new Error('Choose an invoice belonging to this customer and business.');
      }
      const deviceId = await currentDeviceId(db);
      const now = new Date().toISOString();
      return db.transaction(
        'rw',
        [db.payments, db.paymentAllocations, db.documents, db.documentSequences, db.auditLogs, db.syncOutbox],
        () => recordPayment(db as never, {
          businessId: business.id,
          direction: 'RECEIPT',
          customerId: customer.id,
          partySnapshot: {
            id: customer.id,
            name: customer.name,
            gstin: customer.gstin,
            stateCode: customer.stateCode,
            stateName: customer.stateName,
            addressLines: [...customer.billingAddressLines],
            phone: customer.phone,
            email: customer.email,
          },
          paymentDate: input.paymentDate,
          amountPaise: input.amountPaise,
          method: input.method,
          reference: input.reference?.trim() || undefined,
          user: USER,
          now,
          deviceId,
        }, [{ documentId: document.id, amountPaise: input.amountPaise }] satisfies readonly AllocationInput[]),
      );
    },
    onSuccess: async () => {
      await Promise.all([
        client.invalidateQueries({ queryKey: PAYMENTS_QUERY_ROOT }),
        client.invalidateQueries({ queryKey: ['parties'] }),
        client.invalidateQueries({ queryKey: DOCUMENTS_QUERY_ROOT }),
        client.invalidateQueries({ queryKey: PARTIES_QUERY_ROOT }),
      ]);
    },
  });
}
